import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Trash2, Edit3, Lock } from "lucide-react";
import { MONTHS, MONTHS_SHORT, eur } from "./shared";

export function SiteFinanceList({ entries, canEdit, onSave, onDelete, lockedHint }) {
  const now = new Date();
  const empty = () => ({ year: now.getFullYear(), month: now.getMonth() + 1, ca_ht: "", charges: "", notes: "" });
  const [form, setForm] = useState(empty());
  const [open, setOpen] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    await onSave({ year: parseInt(form.year), month: parseInt(form.month), ca_ht: parseFloat(form.ca_ht) || 0, charges: parseFloat(form.charges) || 0, notes: form.notes || "" });
    setOpen(false); setForm(empty());
  };
  const edit = (it) => { setForm({ year: it.year, month: it.month, ca_ht: it.ca_ht, charges: it.charges, notes: it.notes || "" }); setOpen(true); };
  const total = entries.reduce((a, e) => a + (e.ca_ht || 0), 0);

  return (
    <div className="space-y-4" data-testid="site-finance">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="text-sm text-[#495057]">Total saisi : <span className="font-mono font-bold text-[#0A0A0A]" data-testid="site-finance-total">{eur(total)}</span></div>
        {canEdit ? (
          <Button size="sm" onClick={() => { setForm(empty()); setOpen(!open); }} data-testid="site-finance-add" className="h-9 bg-[#002FA7] hover:bg-[#002175] text-white">
            <Plus className="h-3.5 w-3.5 mr-1" /> Saisir un mois
          </Button>
        ) : lockedHint && (
          <div className="flex items-center gap-2 text-xs text-[#495057] bg-[#F8F9FA] border border-[#DEE2E6] px-3 py-2" data-testid="site-finance-locked">
            <Lock className="h-3.5 w-3.5 text-[#868E96]" /> {lockedHint}
          </div>
        )}
      </div>
      {open && canEdit && (
        <form onSubmit={submit} className="bg-[#F8F9FA] border border-[#DEE2E6] p-4 grid sm:grid-cols-5 gap-3 items-end" data-testid="site-finance-form">
          <div className="space-y-1">
            <Label className="text-[10px] uppercase tracking-wider font-semibold">Année</Label>
            <Select value={String(form.year)} onValueChange={(v) => setForm({ ...form, year: v })}>
              <SelectTrigger data-testid="site-finance-year" className="h-10 bg-white"><SelectValue /></SelectTrigger>
              <SelectContent>{[now.getFullYear() - 2, now.getFullYear() - 1, now.getFullYear(), now.getFullYear() + 1].map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label className="text-[10px] uppercase tracking-wider font-semibold">Mois</Label>
            <Select value={String(form.month)} onValueChange={(v) => setForm({ ...form, month: v })}>
              <SelectTrigger data-testid="site-finance-month" className="h-10 bg-white"><SelectValue /></SelectTrigger>
              <SelectContent>{MONTHS.map((m, i) => <SelectItem key={i + 1} value={String(i + 1)}>{m}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label className="text-[10px] uppercase tracking-wider font-semibold">CA HT (€)</Label>
            <Input type="number" step="0.01" value={form.ca_ht} onChange={(e) => setForm({ ...form, ca_ht: e.target.value })} data-testid="site-finance-ca" className="h-10 bg-white" required />
          </div>
          <div className="space-y-1">
            <Label className="text-[10px] uppercase tracking-wider font-semibold">Charges (€)</Label>
            <Input type="number" step="0.01" value={form.charges} onChange={(e) => setForm({ ...form, charges: e.target.value })} data-testid="site-finance-charges" className="h-10 bg-white" />
          </div>
          <Button type="submit" data-testid="site-finance-submit" className="h-10 bg-[#0A0A0A] text-white">Enregistrer</Button>
        </form>
      )}
      <div className="bg-white border border-[#DEE2E6]">
        <div className="grid grid-cols-12 px-4 py-2.5 text-[10px] uppercase tracking-[0.2em] font-bold text-[#868E96] bg-[#F8F9FA] border-b border-[#DEE2E6]">
          <div className="col-span-3">Période</div>
          <div className="col-span-3 text-right">CA HT</div>
          <div className="col-span-2 text-right">Charges</div>
          <div className="col-span-2 text-right">Marge</div>
          <div className="col-span-2 text-right">{canEdit ? "Actions" : ""}</div>
        </div>
        {entries.length === 0 ? (
          <div className="p-6 text-center text-sm text-[#868E96]" data-testid="site-finance-empty">Aucun chiffre d'affaires saisi pour cet établissement.</div>
        ) : entries.map((it) => {
          const marge = (it.ca_ht || 0) - (it.charges || 0);
          return (
            <div key={it.id} className="grid grid-cols-12 px-4 py-2.5 items-center border-b border-[#F1F3F5] last:border-b-0 text-sm" data-testid={`site-finance-row-${it.id}`}>
              <div className="col-span-3 font-mono font-bold">{MONTHS_SHORT[it.month - 1]} {it.year}</div>
              <div className="col-span-3 text-right font-mono">{eur(it.ca_ht)}</div>
              <div className="col-span-2 text-right font-mono text-[#E60000]">-{eur(it.charges)}</div>
              <div className={`col-span-2 text-right font-mono font-bold ${marge >= 0 ? "text-[#059669]" : "text-[#E60000]"}`}>{eur(marge)}</div>
              <div className="col-span-2 flex justify-end gap-1">
                {canEdit && (
                  <>
                    <Button size="sm" variant="outline" onClick={() => edit(it)} className="h-7 px-2" data-testid={`site-finance-edit-${it.id}`}><Edit3 className="h-3 w-3" /></Button>
                    <Button size="sm" variant="outline" onClick={() => onDelete(it.id)} className="h-7 px-2 border-[#E60000] text-[#E60000]" data-testid={`site-finance-delete-${it.id}`}><Trash2 className="h-3 w-3" /></Button>
                  </>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
